import type { QueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import type { Version } from "@/hooks/roster-craete/useRosterVersions";
import type { CurScheduleInfo } from "@/store/scheduleStore";
import { SelectVersionHandler } from "@utils/rosterCreate-util.ts";
import * as req from "@script/req.ts";

function versionsQueryKey(
  year: number,
  month: number,
  selectedGroupId: string | null
) {
  return QUERY_KEYS.roster.versions(year, month, selectedGroupId);
}

export async function invalidateRosterCreateCaches(
  queryClient: QueryClient,
  params: {
    year: number | undefined;
    month: number | undefined;
    selectedGroupId: string | null;
  }
): Promise<void> {
  const { year, month, selectedGroupId } = params;
  if (year === undefined || month === undefined) return;

  await queryClient.invalidateQueries({
    queryKey: QUERY_KEYS.roster.versionsScope(year, month),
  });
  await queryClient.invalidateQueries({
    queryKey: QUERY_KEYS.roster.issuedRosterSnapshotScope(year, month),
  });
  await queryClient.invalidateQueries({
    queryKey: QUERY_KEYS.roster.issuedList(selectedGroupId),
  });
  await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.alarms.list() });
  await queryClient.invalidateQueries({
    queryKey: QUERY_KEYS.roster.scheduleScope(),
  });
  await queryClient.invalidateQueries({
    queryKey: QUERY_KEYS.wanted.status(year, month, selectedGroupId ?? ""),
  });
  await queryClient.invalidateQueries({
    queryKey: QUERY_KEYS.manpower.scope(),
  });
  await queryClient.invalidateQueries({
    queryKey: QUERY_KEYS.roster.prevTailScope(year, month),
  });
}

export async function refetchVersionsAndSelectLatestSchedule(
  queryClient: QueryClient,
  params: {
    year: number;
    month: number;
    selectedGroupId: string | null;
  },
  setCurScheduleInfo: (info: CurScheduleInfo | null) => void
): Promise<void> {
  const queryKey = versionsQueryKey(
    params.year,
    params.month,
    params.selectedGroupId
  );

  const list = await queryClient.fetchQuery<Version[]>({
    queryKey,
    queryFn: () =>
      req.roster_versions(
        params.year,
        params.month,
        params.selectedGroupId ?? undefined
      ),
  });

  const selected =
    list.length > 0 ? SelectVersionHandler(list, "current") : null;

  if (selected) {
    setCurScheduleInfo(selected);
  }
}

export async function invalidateRosterCachesAndSelectLatestSchedule(
  queryClient: QueryClient,
  params: {
    year: number;
    month: number;
    selectedGroupId: string | null;
    isAdmin: boolean;
  },
  setCurScheduleInfo: (info: CurScheduleInfo | null) => void
): Promise<void> {
  await invalidateRosterCreateCaches(queryClient, {
    year: params.year,
    month: params.month,
    selectedGroupId: params.selectedGroupId,
  });
  await refetchVersionsAndSelectLatestSchedule(
    queryClient,
    {
      year: params.year,
      month: params.month,
      selectedGroupId: params.selectedGroupId,
    },
    setCurScheduleInfo
  );
}
